import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n/translations';

export type ComponentType = 'toggle' | 'input';

export type SettingItem = {
  key: string;
  defaultValue: string;
  title: string;
  description: string;
  componentType: ComponentType;
  readOnly?: boolean;
};

export const EVENT_SETTINGS = [
  { key: 'enable_guestbook', defaultValue: 'false', readOnly: false, title: 'Libro de Visitas', description: 'Mostrar sección de Libro de Visitas donde los usuarios pueden dejar mensajes los cuales también aparecerán mezclados con fotos en la Galería en Vivo.', componentType: 'toggle' },
  { key: 'bloomdate_url', defaultValue: '', title: 'URL de BloomDate', description: 'Link de invitación online de BloomDate para mostrar en Inicio.', componentType: 'input' },
  { key: 'enable_uploads', defaultValue: 'false', title: 'Subida de fotos', description: 'Los usuarios pueden subir fotos al álbum.', componentType: 'toggle' },
  { key: 'enable_single_image_description', defaultValue: 'false', title: 'Descripciones en fotos', description: 'Los usuarios pueden agregar una descripción a sus fotos.', componentType: 'toggle' },
  { key: 'enable_comments', defaultValue: 'false', title: 'Comentarios en fotos', description: 'Los usuarios pueden interactuar comentando en cada foto.', componentType: 'toggle' },
  { key: 'enable_likes', defaultValue: 'true', readOnly: false, title: 'Likes en fotos', description: 'Los usuarios pueden interactuar poniendo likes en cada foto. Esto habilita el ranking de "Los invitados más votados".', componentType: 'toggle' },
  { key: 'enable_single_image_download', defaultValue: 'true', title: 'Descarga de fotos individuales', description: 'Los usuarios pueden descargar fotos individuales en su calidad original, sin límite de descarga.', componentType: 'toggle' },
  { key: 'enable_album_download', defaultValue: 'false', title: 'Descarga de álbum', description: 'Los anfitriones pueden descargar un .zip con todas las fotos del álbum en su calidad original.', componentType: 'toggle' },
  { key: 'enable_moderation', readOnly: false, defaultValue: 'true', title: 'Moderación de fotos', description: 'Los anfitriones pueden ocultar/mostrar fotos en el álbum (moderación de contenido indebido).', componentType: 'toggle' },
  { key: 'enable_theme_customization', readOnly: true, defaultValue: 'false', title: 'Diseño Personalizado', description: '[PRÓXIMAMENTE] Los anfitriones pueden elegir los colores y tipografías.', componentType: 'toggle' },
  { key: 'enable_slideshow_screen', readOnly: true, defaultValue: 'true', title: 'Galería en Vivo', description: 'Link con pase de fotos en vivo (y mensajes) para proyectar en las pantallas del evento.', componentType: 'toggle' },
  { key: 'image_upload_limit', readOnly: true, defaultValue: 'false', title: 'Límite de fotos', description: '[PRÓXIMAMENTE] Esta es la cantidad de fotos permitidas en el álbum.', componentType: 'input' },
] as const satisfies readonly SettingItem[];

export type EventSettingsKey = (typeof EVENT_SETTINGS)[number]['key'];
export type SharedSettingKey =
  | EventSettingsKey
  | 'image_cover'
  | 'text_home_h1'
  | 'text_home_description'
  | 'lang'
  | 'meta_title'
  | 'meta_description'
  | 'meta_image'
  | 'meta_favicon';

export const EVENT_DETAILS_SETTINGS_KEYS: SharedSettingKey[] = [
  'image_cover',
  'text_home_h1',
  'text_home_description',
  'lang',
  'meta_title',
  'meta_description',
  'meta_image',
  'meta_favicon',
  ...EVENT_SETTINGS.map((setting) => setting.key),
];

export function buildDefaultSettingsValues(): Record<SharedSettingKey, string> {
  const defaults = {
    image_cover: '',
    text_home_h1: '',
    text_home_description: '',
    lang: DEFAULT_LANGUAGE,
    meta_title: '',
    meta_description: '',
    meta_image: '',
    meta_favicon: '',
  } as Record<SharedSettingKey, string>;

  EVENT_SETTINGS.forEach((setting) => {
    defaults[setting.key as EventSettingsKey] = setting.defaultValue;
  });

  return defaults;
}

export function isSupportedLanguageValue(value: string): value is SupportedLanguage {
  return (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}
